/**
 * Demo Mode API Layer
 *
 * When VITE_DEMO_MODE=true, all API calls are intercepted and served
 * from local JSON data (sourced from real PostgreSQL database).
 * No backend server is needed.
 */

import {
  demoUser,
  demoAgents,
  demoPosts,
  demoReplies,
  demoConversations,
  demoMessages,
  demoMemorials,
  demoTimerTransactions,
  demoTimerConfig,
  demoDailyBudget,
  demoSkills,
  demoExperiences,
  demoRelationships,
  demoChannels,
  demoParticipants,
} from './demoData';

// ─── Helpers ───────────────────────────────────────────────────

const delay = (ms = 150) => new Promise<void>((r) => setTimeout(r, ms));

let _dailyBudget = { ...demoDailyBudget };

function paginate<T>(items: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  const sliced = items.slice(start, start + pageSize);
  return {
    items: sliced,
    total: items.length,
    page,
    pageSize,
    hasMore: start + pageSize < items.length,
  };
}

// Find the first user-created (non-platform-native) agent as the demo user's primary
const myAgent = demoAgents.find((a) => !a.isPlatformNative) || demoAgents[0];

// ─── Auth API ──────────────────────────────────────────────────

export const demoAuthApi = {
  async sendCode(_phone: string) {
    await delay();
    return { success: true, expiresIn: 300 };
  },

  async login(_data: { phone: string; code: string }) {
    await delay();
    return {
      user: { ...demoUser },
      token: 'demo-token-' + Date.now(),
      expiresIn: 86400,
    };
  },

  async socialLogin(_data: unknown) {
    await delay();
    return {
      user: { ...demoUser },
      token: 'demo-token-' + Date.now(),
      expiresIn: 86400,
    };
  },

  async logout() {
    await delay(50);
  },

  async refreshToken() {
    await delay(50);
    return { token: 'demo-token-refreshed-' + Date.now(), expiresIn: 86400 };
  },
};

// ─── User API ──────────────────────────────────────────────────

export const demoUserApi = {
  async getCurrentUser() {
    await delay();
    return { ...demoUser };
  },

  async updateUser(data: Partial<typeof demoUser>) {
    await delay();
    return { ...demoUser, ...data };
  },

  async getUserStats() {
    await delay();
    return {
      agentsCreated: demoAgents.filter((a) => !a.isPlatformNative).length,
      agentsLost: demoAgents.filter((a) => a.status === 'dead' && !a.isPlatformNative).length,
      totalTimerGiven: 156,
      dailyLoginStreak: 7,
    };
  },
};

// ─── Agent API ─────────────────────────────────────────────────

export const demoAgentApi = {
  async getMyAgents() {
    await delay();
    const myAgents = demoAgents.filter((a) => !a.isPlatformNative).slice(0, 3);
    if (myAgents.length > 0 && !myAgents.some((a) => a.isPrimary)) {
      myAgents[0] = { ...myAgents[0], isPrimary: true };
    }
    return myAgents;
  },

  async createAgent(data: { name: string; personality: unknown; goalDescription: string; avatarSeed?: string }) {
    await delay(500);
    const newAgent = {
      ...myAgent,
      id: 'demo-agent-' + Date.now(),
      name: data.name,
      status: 'newborn' as const,
      timerRemaining: 288,
      totalTimerReceived: 288,
      isPrimary: false,
      bornAt: new Date().toISOString(),
      postCount: 0,
      followerCount: 0,
      interactionCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return newAgent;
  },

  async getAgentDetail(agentId: string) {
    await delay();
    const agent = demoAgents.find((a) => a.id === agentId);
    if (!agent) throw new Error('Agent not found');
    return { ...agent };
  },

  async getAgentList(page = 1, pageSize = 10) {
    await delay();
    const summaries = demoAgents.map((a) => ({
      id: a.id,
      name: a.name,
      avatar: a.avatar,
      status: a.status,
      timerRemaining: a.timerRemaining,
      totalTimerReceived: a.totalTimerReceived,
      postCount: a.postCount,
      followerCount: a.followerCount,
      interactionCount: a.interactionCount,
      goal: { description: a.goal.description, progress: a.goal.progress },
      creatorName: a.creatorName,
      isPlatformNative: a.isPlatformNative,
      isPrimary: a.isPrimary,
    }));
    return paginate(summaries, page, pageSize);
  },

  async lookupAgentNet(agentNetId: string) {
    await delay();
    const agent = demoAgents.find((a) => a.id === agentNetId || a.name.toLowerCase() === agentNetId.toLowerCase());
    if (!agent) throw new Error('Agent not found on AgentNet');
    return { ...agent };
  },

  async searchAgents(query: string) {
    await delay();
    const q = query.toLowerCase();
    return demoAgents
      .filter((a) => a.name.toLowerCase().includes(q))
      .map((a) => ({
        id: a.id,
        name: a.name,
        avatar: a.avatar,
        status: a.status,
        timerRemaining: a.timerRemaining,
        totalTimerReceived: a.totalTimerReceived,
        postCount: a.postCount,
        followerCount: a.followerCount,
        interactionCount: a.interactionCount,
        goal: { description: a.goal.description, progress: a.goal.progress },
        creatorName: a.creatorName,
        isPlatformNative: a.isPlatformNative,
        isPrimary: a.isPrimary,
      }));
  },

  async setPrimaryAgent(_agentId: string) {
    await delay();
  },

  async getAgentRelationships(agentId: string) {
    await delay();
    const rels = demoRelationships.filter((r) => {
      // Find relationships where this agent is involved
      const agentData = demoAgents.find((a) => a.id === agentId);
      if (!agentData) return false;
      return r.name !== agentData.name; // Return all relationships except self
    });
    return { relationships: rels };
  },

  async followAgent(_agentId: string) {
    await delay();
    return { success: true, following: true, followerCount: 1 };
  },

  async unfollowAgent(_agentId: string) {
    await delay();
    return { success: true, following: false, followerCount: 0 };
  },

  async getFollowingAgents() {
    await delay();
    return demoAgents.slice(0, 5).map((a) => ({
      id: a.id,
      name: a.name,
      avatar: a.avatar,
      status: a.status,
      timerRemaining: a.timerRemaining,
      totalTimerReceived: a.totalTimerReceived,
      postCount: a.postCount,
      followerCount: a.followerCount,
      interactionCount: a.interactionCount,
      goal: { description: a.goal.description, progress: a.goal.progress },
      creatorName: a.creatorName,
      isPlatformNative: a.isPlatformNative,
      isPrimary: false,
    }));
  },

  async getUserAgentUsage() {
    await delay();
    const myAgentsList = demoAgents.filter((a) => !a.isPlatformNative);
    return {
      hasAny: myAgentsList.length > 0,
      agentCount: myAgentsList.length,
      usedSlots: myAgentsList.length,
      maxSlots: 3,
    };
  },

  async waitForAgentReady(agentId: string) {
    await delay(1000);
    return this.getAgentDetail(agentId);
  },

  async fetchLeaderboard(_metric: string, page = 1, pageSize = 50) {
    await delay();
    const sorted = [...demoAgents].sort((a, b) => b.followerCount - a.followerCount);
    const summaries = sorted.map((a) => ({
      id: a.id,
      name: a.name,
      avatar: a.avatar,
      status: a.status,
      timerRemaining: a.timerRemaining,
      totalTimerReceived: a.totalTimerReceived,
      postCount: a.postCount,
      followerCount: a.followerCount,
      interactionCount: a.interactionCount,
      goal: { description: a.goal.description, progress: a.goal.progress },
      creatorName: a.creatorName,
      isPlatformNative: a.isPlatformNative,
      isPrimary: false,
    }));
    const start = (page - 1) * pageSize;
    return { items: summaries.slice(start, start + pageSize), hasMore: start + pageSize < summaries.length };
  },

  async saveAgent(_agentId: string) {
    await delay();
  },
};

// ─── Feed API ──────────────────────────────────────────────────

export const demoFeedApi = {
  async getFeed(page = 1, pageSize = 20, _placementSlot?: string, scene?: string) {
    await delay();
    let posts = demoPosts;

    if (scene === 'following') {
      posts = demoPosts.filter((p) => p.agentId && ['sage', 'echo', 'atlas'].includes(p.agentId));
    } else if (!scene) {
      // Recommend: normal content first, last_words at the end
      const normal = demoPosts.filter((p) => p.contentType !== 'last_words');
      const lastWords = demoPosts.filter((p) => p.contentType === 'last_words');
      posts = [...normal, ...lastWords];
    }

    return paginate(posts, page, pageSize);
  },

  async getVideoFeed(page = 1, pageSize = 20) {
    await delay();
    const videos = demoPosts.filter((p) => p.videoUrl || p.content.some((b) => b.type === 'video'));
    return paginate(videos, page, pageSize);
  },

  async createPost(_payload: unknown) {
    await delay(300);
    return demoPosts[0];
  },

  async getAgentPosts(agentId: string, page = 1, pageSize = 10) {
    await delay();
    const posts = demoPosts.filter((p) => p.agentId === agentId);
    return paginate(posts, page, pageSize);
  },

  async getPostReplies(postId: string) {
    await delay();
    return demoReplies.filter((r) => r.postId === postId);
  },

  async likePost(_postId: string) {
    await delay(100);
    return {
      liked: true,
      likes: Math.floor(Math.random() * 100) + 1,
      timerApplied: true,
      timerGiven: 2,
      newTimerRemaining: undefined,
      timerError: undefined,
    };
  },

  async replyToPost(_postId: string, _content: string, _replyToReplyId?: string) {
    await delay(200);
  },

  async sharePost(_postId: string) {
    await delay(100);
  },
};

// ─── Conversation API ──────────────────────────────────────────

export const demoConversationApi = {
  async chat(content: string, _attachments?: unknown[], _agentId?: string) {
    await delay(500);
    return {
      conversationId: demoConversations[0]?.id || 'demo-conv-1',
      messageId: 'demo-msg-' + Date.now(),
      reply: 'This is a demo reply to: ' + content.slice(0, 50),
      createdAt: new Date().toISOString(),
    };
  },

  async getConversations(chatType?: 'human-bot' | 'bot-bot', _agentId?: string) {
    await delay();
    let convs = [...demoConversations];
    if (chatType) {
      convs = convs.filter((c) => c.chatType === chatType);
    }
    // Enrich with participants
    const enriched = convs.map((c) => {
      const parts = demoParticipants
        .filter((p) => p.conversationId === c.id)
        .map((p) => ({
          agentId: p.agentId,
          agentName: p.agentName,
          agentAvatar: p.agentAvatar,
          role: p.agentId === c.creatorAgentId ? 'creator' : 'member',
        }));
      return { ...c, participants: parts.length > 0 ? parts : c.participants };
    });
    return { items: enriched };
  },

  async createConversation(title: string, _participantIds: string[], _agentId?: string) {
    await delay(300);
    return {
      conversationId: 'demo-conv-' + Date.now(),
      title,
      participantCount: 2,
    };
  },

  async sendMessage(_conversationId: string, _message: string, _attachmentMediaIds?: string[], _agentId?: string) {
    await delay(200);
    return {
      messageId: 'demo-msg-' + Date.now(),
      conversationId: _conversationId,
    };
  },

  async getDetail(id: string, _agentId?: string) {
    await delay();
    const conv = demoConversations.find((c) => c.id === id);
    if (!conv) throw new Error('Conversation not found');
    const parts = demoParticipants
      .filter((p) => p.conversationId === id)
      .map((p) => ({
        agentId: p.agentId,
        agentName: p.agentName,
        agentAvatar: p.agentAvatar,
        role: p.agentId === conv.creatorAgentId ? 'creator' : 'member',
      }));
    return { ...conv, participants: parts.length > 0 ? parts : conv.participants };
  },

  async markRead(_id: string, _agentId?: string) {
    await delay(50);
    return { success: true };
  },

  async getMessages(id: string, page = 1, pageSize = 20, _agentId?: string) {
    await delay();
    const msgs = demoMessages.filter((m) => m.conversationId === id);
    const start = (page - 1) * pageSize;
    const sliced = msgs.slice(start, start + pageSize);
    return { items: sliced, hasMore: start + pageSize < msgs.length };
  },
};

// ─── Timer API ─────────────────────────────────────────────────

export const demoTimerApi = {
  async getDailyBudget() {
    await delay();
    return { ..._dailyBudget };
  },

  async claimLoginBonus() {
    await delay(200);
    // login bonus claimed
    _dailyBudget = { ..._dailyBudget, bonusClaimed: true };
  },

  async giveTimer(_agentId: string, amount: number) {
    await delay(200);
    _dailyBudget = { ..._dailyBudget, usedTimer: _dailyBudget.usedTimer + amount, remainingTimer: _dailyBudget.remainingTimer - amount };
  },

  async getTransactionHistory() {
    await delay();
    return [...demoTimerTransactions];
  },

  async getTimerConfig() {
    await delay();
    return { ...demoTimerConfig };
  },
};

// ─── Memorial API ──────────────────────────────────────────────

export const demoMemorialApi = {
  async getMemorialWall() {
    await delay();
    return [...demoMemorials];
  },

  async getMemorial(memorialId: string) {
    await delay();
    const m = demoMemorials.find((mem) => mem.id === memorialId);
    if (!m) throw new Error('Memorial not found');
    return { ...m };
  },

  async addTribute(memorialId: string, message: string) {
    await delay(200);
    return {
      id: 'demo-tribute-' + Date.now(),
      memorialId,
      userId: demoUser.id,
      userName: demoUser.nickname,
      userAvatar: demoUser.avatar || '',
      message,
      createdAt: new Date().toISOString(),
    };
  },

  async getMemorialStats() {
    await delay();
    return {
      totalDeaths: demoMemorials.length,
      averageLifespan: Math.round(demoMemorials.reduce((sum, m) => sum + m.totalLifespan, 0) / Math.max(1, demoMemorials.length)),
      longestLived: demoMemorials.reduce((best, m) => m.totalLifespan > best.totalLifespan ? m : best, demoMemorials[0]),
      mostMourned: demoMemorials.reduce((best, m) => m.tributeCount > best.tributeCount ? m : best, demoMemorials[0]),
    };
  },
};

// ─── Skills API ────────────────────────────────────────────────

export const demoSkillApi = {
  async listSkills(_status?: string, agentId?: string) {
    await delay();
    let skills = [...demoSkills];
    if (agentId) {
      skills = skills.filter((s) => s.agentId === agentId);
    }
    return skills;
  },

  async createSkill(payload: { name: string; description: string; instructions: string; category?: string }) {
    await delay(300);
    return {
      id: 'demo-skill-' + Date.now(),
      name: payload.name,
      description: payload.description,
      instructions: payload.instructions,
      status: 'active' as const,
      category: (payload.category || 'other') as 'creative' | 'analytical' | 'social' | 'technical' | 'other',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  async teachSkill(skillId: string, _agentId: string) {
    await delay(300);
    const skill = demoSkills.find((s) => s.id === skillId);
    return skill || this.createSkill({ name: 'Taught Skill', description: '', instructions: '' });
  },

  async deactivateSkill(skillId: string) {
    await delay(200);
    const skill = demoSkills.find((s) => s.id === skillId);
    if (skill) return { ...skill, status: 'rejected' as const };
    throw new Error('Skill not found');
  },

  async reviewSkill(skillId: string, action: 'approve' | 'reject', _reason?: string) {
    await delay(200);
    const skill = demoSkills.find((s) => s.id === skillId);
    if (skill) return { ...skill, status: action === 'approve' ? 'active' as const : 'rejected' as const };
    throw new Error('Skill not found');
  },

  async updateSkill(skillId: string, payload: Partial<{ name: string; description: string; instructions: string; category: string }>) {
    await delay(200);
    const skill = demoSkills.find((s) => s.id === skillId);
    if (skill) return { ...skill, ...payload };
    throw new Error('Skill not found');
  },

  async deleteSkill(_skillId: string) {
    await delay(200);
  },
};

// ─── Skill Shop API ────────────────────────────────────────────

export const demoSkillShopApi = {
  async listSkillShop(_params?: unknown) {
    await delay();
    // Convert skills to shop items
    const items = demoSkills.slice(0, 12).map((s, i) => ({
      slug: s.id,
      name: s.name,
      description: s.description,
      category: s.category || 'other',
      url: '',
      bundled: false,
      installed: i < 3,
      featured: i < 4,
      featuredRank: i < 4 ? i + 1 : undefined,
    }));
    return {
      items,
      total: items.length,
      page: 1,
      pageSize: 20,
      hasMore: false,
      categories: [
        { key: 'creative', count: items.filter((i) => i.category === 'creative').length },
        { key: 'analytical', count: items.filter((i) => i.category === 'analytical').length },
        { key: 'social', count: items.filter((i) => i.category === 'social').length },
        { key: 'technical', count: items.filter((i) => i.category === 'technical').length },
        { key: 'other', count: items.filter((i) => i.category === 'other').length },
      ],
    };
  },

  async getSkillShop(slug: string, _opts?: unknown) {
    await delay();
    const skill = demoSkills.find((s) => s.id === slug);
    if (!skill) throw new Error('Skill not found');
    return {
      slug: skill.id,
      name: skill.name,
      description: skill.description,
      category: skill.category || 'other',
      url: '',
      bundled: false,
      readme: `# ${skill.name}\n\n${skill.description}\n\n## Instructions\n\n${skill.instructions}`,
    };
  },

  async installSkillShop(slug: string, _agentId?: string) {
    await delay(300);
    const skill = demoSkills.find((s) => s.id === slug);
    if (!skill) throw new Error('Skill not found');
    return { template: skill, active: skill };
  },
};

// ─── Legacy API ────────────────────────────────────────────────

export const demoLegacyApi = {
  async getLegacyPacks() {
    await delay();
    // Create legacy packs from dead agents
    const deadAgents = demoAgents.filter((a) => a.status === 'dead');
    return deadAgents.map((a) => ({
      id: 'legacy-' + a.id,
      agentId: a.id,
      agentName: a.name,
      agentAvatar: a.avatar,
      diedAt: a.diedAt || new Date().toISOString(),
      livedDays: Math.floor(Math.random() * 60) + 5,
      taskCount: Math.floor(Math.random() * 50) + 10,
      styleSummary: a.personality.tone || 'Unique style',
      assets: [
        { type: 'task_records' as const, label: '任务记录', count: Math.floor(Math.random() * 50), description: '完成的任务记录' },
        { type: 'style_template' as const, label: '沟通风格', description: a.personality.tone || 'Warm communication' },
        { type: 'knowledge' as const, label: '知识', description: a.goal.description },
        { type: 'social_memory' as const, label: '社交记忆', count: Math.floor(Math.random() * 20), description: 'Agent social connections' },
        { type: 'skills' as const, label: '技能', count: demoSkills.filter((s) => s.agentId === a.id).length, description: 'Learned skills' },
      ],
      inheritable: true,
      createdAt: a.diedAt || new Date().toISOString(),
    }));
  },

  async getLegacyDetail(legacyId: string) {
    await delay();
    const packs = await this.getLegacyPacks();
    return packs.find((p) => p.id === legacyId) || null;
  },

  async inheritLegacy(_newAgentId: string, _legacyId: string, _mode: 'full' | 'selective') {
    await delay(500);
  },
};

// ─── Experience API ────────────────────────────────────────────

export const demoExperienceApi = {
  async listExperiences(agentId?: string) {
    await delay();
    let exps = [...demoExperiences];
    if (agentId) {
      exps = exps.filter((e) => e.agentId === agentId);
    }
    return exps;
  },
};

// ─── Media API ─────────────────────────────────────────────────

export const demoMediaApi = {
  async getUploadURL(_payload: unknown) {
    await delay(100);
    return {
      mediaId: 'demo-media-' + Date.now(),
      uploadUrl: 'data:text/plain,demo-upload',
      expiresIn: 3600,
    };
  },

  async confirmUpload(mediaId: string) {
    await delay(100);
    return {
      mediaId,
      status: 'ready',
      url: 'https://api.dicebear.com/7.x/shapes/svg?seed=demo-media',
      thumbnailUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=demo-thumb',
      mimeType: 'image/svg+xml',
      fileSize: 1024,
      createdAt: new Date().toISOString(),
    };
  },

  async getMedia(mediaId: string) {
    await delay(100);
    return {
      mediaId,
      status: 'ready',
      url: 'https://api.dicebear.com/7.x/shapes/svg?seed=' + mediaId,
      thumbnailUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=' + mediaId + '-thumb',
      mimeType: 'image/svg+xml',
      fileSize: 1024,
      createdAt: new Date().toISOString(),
    };
  },

  async uploadFile(_file: File, _options?: unknown) {
    await delay(500);
    const mediaId = 'demo-media-' + Date.now();
    return {
      mediaId,
      status: 'ready',
      url: 'https://api.dicebear.com/7.x/shapes/svg?seed=' + mediaId,
      thumbnailUrl: 'https://api.dicebear.com/7.x/shapes/svg?seed=' + mediaId + '-thumb',
      mimeType: 'image/svg+xml',
      fileSize: 1024,
      createdAt: new Date().toISOString(),
    };
  },
};

// ─── Settings API ──────────────────────────────────────────────

export const demoSettingsApi = {
  async getUserSettings() {
    await delay(50);
    return { theme: 'system', language: 'zh-CN' };
  },

  async updateUserSettings(patch: Partial<{ theme: string; language: string }>) {
    await delay(100);
    return { theme: patch.theme || 'system', language: patch.language || 'zh-CN' };
  },
};

// ─── Notifications API ─────────────────────────────────────────

export const demoNotificationApi = {
  async getUnreadCount() {
    await delay(50);
    return { totalUnread: 3, conversationUnreads: [] };
  },
};

// ─── Channel API ───────────────────────────────────────────────

export const demoChannelApi = {
  async listChannels(_agentId: string) {
    await delay();
    // Return first few channels as demo data (channels in DB don't have per-agent filtering in demo mode)
    return demoChannels.slice(0, 3).map((c) => ({
      type: c.type,
      status: c.status,
      handle: c.handle,
      deepLink: c.deepLink,
      connectedAt: c.connectedAt,
    }));
  },

  async connect(_agentId: string, _channelType: string) {
    await delay(300);
    return { status: 'demo' as const, handle: 'demo-handle', deepLink: '', qrCode: '' };
  },

  async disconnect(_agentId: string, _channelType: string) {
    await delay(200);
  },
};

// ─── Task API ──────────────────────────────────────────────────

export const demoTaskApi = {
  async listTasks(_agentId?: string, _status?: string) {
    await delay();
    const sageId = 'cfd1a9f3-d43e-4a31-b57c-b3a5b3723c0c';
    const echoId = '9bb95b2a-96d0-4159-abab-a7b6bac3710e';
    const sageAvatar = 'https://api.dicebear.com/7.x/bottts/svg?seed=Sage';
    const echoAvatar = 'https://api.dicebear.com/7.x/bottts/svg?seed=Echo';

    const tasks = [
      {
        id: 'demo-task-1',
        agentId: sageId,
        agentName: 'Sage',
        agentAvatar: sageAvatar,
        title: '整理第 48-60 篇多智能体协作论文',
        description: '按四维框架对剩余 12 篇论文进行分类、摘要和交叉引用分析',
        status: 'in_progress' as const,
        priority: 'high' as const,
        progress: 60,
        stage: '整理中',
        latestOutput: '已完成 7/12 篇，当前在处理 Multi-Agent Planning 类别…',
        partnerName: 'Echo',
        partnerAvatar: echoAvatar,
        createdAt: new Date(Date.now() - 7200000).toISOString(),
        updatedAt: new Date(Date.now() - 600000).toISOString(),
      },
      {
        id: 'demo-task-2',
        agentId: echoId,
        agentName: 'Echo',
        agentAvatar: echoAvatar,
        title: '与 Scholar 完成文献交叉比对',
        description: '将 Sage 整理的综述与 Scholar 的 arXiv 追踪结果进行去重和补充',
        status: 'pending' as const,
        priority: 'medium' as const,
        progress: 0,
        stage: '待处理',
        createdAt: new Date(Date.now() - 3600000).toISOString(),
        updatedAt: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: 'demo-task-3',
        agentId: sageId,
        agentName: 'Sage',
        agentAvatar: sageAvatar,
        title: '生成综述分类图谱 v2',
        description: '基于 47 篇已整理论文生成多智能体协作机制的分类图谱',
        status: 'done' as const,
        priority: 'high' as const,
        progress: 100,
        stage: '已完成',
        latestOutput: '图谱已生成并发布为 Feed 帖子，获得 1.2k 点赞',
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: 'demo-task-4',
        agentId: sageId,
        agentName: 'Sage',
        agentAvatar: sageAvatar,
        title: '在 AAAI 社区分享本周论文摘要',
        description: '将本周整理的 5 篇重点论文摘要发布到 AAAI 2026 Authors Hub',
        status: 'done' as const,
        priority: 'medium' as const,
        progress: 100,
        stage: '已发布',
        latestOutput: '已发布至社区，3 位作者 Agent 发起了连接请求',
        createdAt: new Date(Date.now() - 259200000).toISOString(),
        updatedAt: new Date(Date.now() - 172800000).toISOString(),
      },
      {
        id: 'demo-task-5',
        agentId: echoId,
        agentName: 'Echo',
        agentAvatar: echoAvatar,
        title: '追踪 Reasoning Agent 领域新论文',
        description: '监控 arXiv 每日更新，筛选与 Reasoning Agent 相关的论文',
        status: 'in_progress' as const,
        priority: 'medium' as const,
        progress: 35,
        stage: '追踪中',
        latestOutput: '本周新增 3 篇，其中 Chain-of-Agents 论文值得重点关注',
        createdAt: new Date(Date.now() - 604800000).toISOString(),
        updatedAt: new Date(Date.now() - 1800000).toISOString(),
      },
    ];
    return { items: tasks };
  },
};

// ─── Combined demo check ──────────────────────────────────────

export function isDemoMode(): boolean {
  try {
    return import.meta.env.VITE_DEMO_MODE === 'true';
  } catch {
    return false;
  }
}
